import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Circle,
  Settings,
  TrendingUp,
  CheckCircle,
  PauseCircle,
  XCircle,
  Archive
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectStatusMasterService from '../../services/projectStatusMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProjectStatusMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCount: 0,
    categoryStats: [],
    typeStats: [],
    recentCount: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedStatusData, setSelectedStatusData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [statusResponse, statsResponse] = await Promise.all([
        projectStatusMasterService.getAllProjectStatusMasters(),
        projectStatusMasterService.getProjectStatusMasterStats()
      ])

      if (statusResponse.success) {
        setData(statusResponse.data)
        
        // Show toast if no data
        if (statusResponse.data.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No project status masters found. Create your first one!')
        }
      } else {
        toast.error(statusResponse.message || 'Failed to load project status masters')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view project status masters.')
      } else {
        toast.error('Failed to load project status masters. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    navigate('/projects/project-status-master/new')
  }

  const handleEdit = (row) => {
    // Clean the data to remove non-serializable objects
    const cleanRowData = {
      _id: row._id,
      id: row.id,
      company: row.company,
      statusCode: row.statusCode,
      statusName: row.statusName,
      statusDescription: row.statusDescription,
      statusCategory: row.statusCategory,
      statusType: row.statusType,
      statusColor: row.statusColor,
      statusIcon: row.statusIcon,
      isDefault: row.isDefault,
      sortOrder: row.sortOrder,
      allowsTaskCreation: row.allowsTaskCreation,
      allowsTimeTracking: row.allowsTimeTracking,
      allowsBilling: row.allowsBilling,
      requiresApproval: row.requiresApproval,
      allowedTransitions: row.allowedTransitions,
      notifyOnStatusChange: row.notifyOnStatusChange,
      notifyStakeholders: row.notifyStakeholders,
      averageDurationInStatus: row.averageDurationInStatus,
      revisionHistory: row.revisionHistory,
      currentRevision: row.currentRevision,
      createdBy: row.createdBy,
      updatedBy: row.updatedBy,
      isArchived: row.isArchived,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt
    }
    
    navigate('/projects/project-status-master/edit', { 
      state: { statusData: cleanRowData } 
    })
  }

  const handleDelete = async (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await projectStatusMasterService.deleteProjectStatusMaster(deleteItem._id)
      
      if (response.success) {
        toast.success('Project status master deleted successfully!')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete project status master')
      }
    } catch (error) {
      console.error('Error deleting project status master:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete project status masters.')
      } else {
        toast.error('Failed to delete project status master. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleViewRevisions = (row) => {
    setSelectedStatusData(row)
    setShowRevisionHistory(true)
  }

  const getStatusIcon = (statusCategory) => {
    switch (statusCategory) {
      case 'Planning':
        return <Circle size={16} style={{ color: '#3B82F6' }} />
      case 'Active':
        return <CheckCircle size={16} style={{ color: '#10B981' }} />
      case 'On Hold':
        return <PauseCircle size={16} style={{ color: '#F59E0B' }} />
      case 'Completed':
        return <CheckCircle size={16} style={{ color: '#059669' }} />
      case 'Cancelled':
        return <XCircle size={16} style={{ color: '#DC2626' }} />
      case 'Archived':
        return <Archive size={16} style={{ color: '#6B7280' }} />
      default:
        return <Circle size={16} style={{ color: '#6B7280' }} />
    }
  }

  const getStatusColor = (statusColor) => {
    return statusColor || '#6B7280'
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'projectStatusCode',
      label: 'Status Code',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div 
            style={{ 
              width: '12px', 
              height: '12px', 
              borderRadius: '50%', 
              backgroundColor: getStatusColor(row.statusColor) 
            }} 
          />
          <span style={{ fontWeight: '600', fontFamily: 'monospace' }}>{value}</span>
        </div>
      )
    },
    {
      key: 'projectStatusName',
      label: 'Status Name',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getStatusIcon(row.projectStatusCategory)}
          <span style={{ fontWeight: '500' }}>{value}</span>
        </div>
      )
    },
    {
      key: 'projectStatusCategory',
      label: 'Category',
      sortable: true,
      render: (value) => (
        <span style={{ 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: '#f3f4f6',
          color: '#374151'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'projectStatusType',
      label: 'Type',
      sortable: true,
      render: (value) => (
        <span style={{ 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: '#E8E1F5',
          color: '#0369a1'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'sortOrder',
      label: 'Order',
      sortable: true,
      render: (value) => (
        <span style={{ 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: '#fef3c7',
          color: '#92400e'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'isDefault',
      label: 'Default',
      sortable: true,
      render: (value) => (
        value ? (
          <span style={{ 
            padding: '4px 8px', 
            borderRadius: '4px', 
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: '#dcfce7',
            color: '#166534'
          }}>
            Yes
          </span>
        ) : (
          <span style={{ 
            padding: '4px 8px', 
            borderRadius: '4px', 
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: '#f3f4f6',
            color: 'var(--color-text-secondary, #5B6478)'
          }}>
            No
          </span>
        )
      )
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Project Status Master', path: '/projects/project-status-master', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Status Master</h1>
          <p>Manage project status configurations and workflow settings</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleCreate}>
            <Plus size={16} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCount}</div>
          <div className="metric-title">Total Statuses</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.recentCount}</div>
          <div className="metric-title">Recent (30 days)</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Circle size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.categoryStats.length}</div>
          <div className="metric-title">Categories</div>
          <div className="metric-subtitle">Active categories</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.typeStats.length}</div>
          <div className="metric-title">Types</div>
          <div className="metric-subtitle">Status types</div>
        </div>
      </div>

      {/* Project Status Master Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleViewRevisions}
          onDelete={handleDelete}
          onRevisionHistory={handleViewRevisions}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search project statuses..."
          emptyMessage="No project statuses found. Create your first project status!"
          noCard={true}
        />
      </div>

      {/* Revision History Modal */}
      {showRevisionHistory && selectedStatusData && (
        <RevisionHistoryModal
          isOpen={showRevisionHistory}
          onClose={() => setShowRevisionHistory(false)}
          data={selectedStatusData}
          title="Project Status Master Revision History"
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Project Status Master"
        message={`Are you sure you want to delete "${deleteItem?.statusName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProjectStatusMaster